import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import { Box, Flex } from '../../grid';
import { Image } from '../../image';

const IMAGE_SIZE = '56px';

const Row = styled(Flex).attrs({ as: 'li' })`
  border-bottom: 1px solid ${props => props.theme.colors.warmWhite3};
  list-style: none;

  /* Divider lines bound the whole list (top + bottom), not just between rows. */
  &:first-child {
    border-top: 1px solid ${props => props.theme.colors.warmWhite3};
  }
`;

const ImageWrapper = styled(Box)`
  border-radius: 4px;
  flex-shrink: 0;
  overflow: hidden;
`;

const ImagePlaceholder = styled(Flex)`
  background: ${props => props.theme.colors.grey100};
  border-radius: 4px;
`;

// Matches the web app's existing "no image" treatment (see
// product-detail-modal/choice-picker/placeholder) — a chilli glyph centred in
// the image slot. The asset is scaled down to suit the 56px gift tile.
const PlaceholderGlyph = styled.img`
  height: 28px;
  width: auto;
`;

const StyledImage = styled(Image)`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const Name = styled.span`
  color: ${props => props.theme.colors.black};
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
`;

const SelectButton = styled.button`
  appearance: none;
  background: ${props => props.theme.colors.black};
  border: none;
  border-radius: 999px;
  color: ${props => props.theme.colors.white};
  cursor: pointer;
  flex-shrink: 0;
  font-family: Barlow, sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.48px;
  line-height: 1.35;
  padding: 10px 20px;
  text-transform: uppercase;

  &:focus {
    outline: 2px solid ${props => props.theme.colors.black};
    outline-offset: 2px;
  }
`;

const GiftOption = ({ variant = 'product', name, image = null, isSelected = false, onSelect }) => {
  if (variant === 'freeDelivery') {
    return null;
  }

  return (
    <Row alignItems="center" justifyContent="space-between" px="16px" py="12px" data-testid="gift-option">
      <Flex alignItems="center" flexGrow={1} minWidth={0}>
        <ImageWrapper width={IMAGE_SIZE} height={IMAGE_SIZE} mr="12px">
          {image ? (
            <StyledImage src={image} alt={name} />
          ) : (
            <ImagePlaceholder
              width={IMAGE_SIZE}
              height={IMAGE_SIZE}
              alignItems="center"
              justifyContent="center"
              data-testid="gift-option-image-placeholder"
            >
              <PlaceholderGlyph src="/static/images/chili.png" alt="" />
            </ImagePlaceholder>
          )}
        </ImageWrapper>
        <Flex flexDirection="column" flexGrow={1} minWidth={0}>
          <Name>{name}</Name>
        </Flex>
      </Flex>
      <SelectButton
        type="button"
        onClick={onSelect}
        aria-pressed={isSelected}
        data-testid="gift-option-select"
        ml="12px"
      >
        {isSelected ? 'Selected' : 'Select'}
      </SelectButton>
    </Row>
  );
};

GiftOption.propTypes = {
  variant: PropTypes.oneOf(['product', 'freeDelivery']),
  name: PropTypes.string.isRequired,
  image: PropTypes.string,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

GiftOption.defaultProps = {
  variant: 'product',
  image: null,
  isSelected: false,
};

export { GiftOption };
